import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import {
  Keyboard,
  Modal as NativeModal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
  type PressableProps
} from 'react-native'
import { useQuery } from '@apollo/client'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { SheetManager } from 'react-native-actions-sheet'
import Animated, { runOnJS, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { PAYMENT_METHODS, PaymentKind } from '@/constants/common'
import { GET_USER, GET_USER_PAYMENT_METHODS } from '@/constants/graphql/queries'
import type { PaymentMethod } from '@/types/common'
import { cn } from '@/utils/cn'
import { groupPaymentMethods } from '@/utils/payment'
import { AuthContext } from '@/stores/auth'
import { Banner, Button, useModal, useToast } from '@/components/base'
import { Check, CheckCircle, ChevronRight, ServerError, Spinner, Wallet } from '@/components/icon'
import NavigationBar from '@/components/NavigationBar'
import SectionHeader from '@/components/SectionHeader'
import SetTxPasswordModal from '@/components/SetTxPasswordModal'

type CurrentBuyPayment = {
  id: string
  kind: PaymentKind
}

type CurrentSellPayment = {
  [key in PaymentKind]?: {
    id: string
    kind: PaymentKind
  }
}

interface SelectPaymentMethodState {
  type: 'buy' | 'sell'
  setType: (value: 'buy' | 'sell') => void
  selectedBuyPayment: undefined | CurrentBuyPayment
  setSelectedBuyPayment: (value: undefined | CurrentBuyPayment) => void
  selectedBuyPaymentText: string
  setSelectedBuyPaymentText: (value: string) => void
  selectedSellPayment: CurrentSellPayment
  setSelectedSellPayment: (value: CurrentSellPayment) => void
  open: () => void
  close: () => void
  offset: any
  isHidden: boolean
  show: () => void
  hide: () => void
}

export const SelectPaymentMethodContext = createContext<SelectPaymentMethodState>({
  type: 'buy',
  setType: () => {},
  selectedBuyPayment: undefined,
  setSelectedBuyPayment: () => {},
  selectedBuyPaymentText: '',
  setSelectedBuyPaymentText: () => {},
  selectedSellPayment: {},
  setSelectedSellPayment: () => {},
  open: () => {},
  close: () => {},
  offset: 0,
  isHidden: false,
  show: () => {},
  hide: () => {}
})

interface SelectPaymentMethodProviderProps {
  children: ReactNode
}

export function SelectPaymentMethodProvider({ children }: SelectPaymentMethodProviderProps) {
  const { width: deviceWidth } = useWindowDimensions()
  const [type, setType] = useState<'buy' | 'sell'>('buy')
  const [selectedBuyPayment, setSelectedBuyPayment] = useState<undefined | CurrentBuyPayment>(undefined)
  const [selectedBuyPaymentText, setSelectedBuyPaymentText] = useState('')
  const [selectedSellPayment, setSelectedSellPayment] = useState<CurrentSellPayment>({})
  const [isOpen, setIsOpen] = useState(false)
  const [isHidden, setIsHidden] = useState(false)
  const offset = useSharedValue(deviceWidth)
  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => {
    offset.value = withTiming(deviceWidth, { duration: 200 }, () => {
      runOnJS(setIsOpen)(false)
    })
  }, [])
  const show = useCallback(() => {
    setIsHidden(false)
    if (Platform.OS !== 'web') {
      offset.value = deviceWidth
      setIsOpen(true)
    }
  }, [deviceWidth])
  const hide = useCallback(() => {
    if (Platform.OS !== 'web') {
      offset.value = deviceWidth
      setIsHidden(true)
      setIsOpen(false)
      return
    }
    setTimeout(() => setIsHidden(true), 200)
  }, [deviceWidth])
  const value = {
    type,
    setType,
    selectedBuyPayment,
    setSelectedBuyPayment,
    selectedBuyPaymentText,
    setSelectedBuyPaymentText,
    selectedSellPayment,
    setSelectedSellPayment,
    open,
    close,
    offset,
    isHidden,
    show,
    hide
  }

  return (
    <SelectPaymentMethodContext.Provider value={value}>
      {children}
      {isOpen &&
        (Platform.OS === 'web' ? (
          <SelectPaymentMethodScreen />
        ) : (
          <NativeModal
            animationType={'none'}
            navigationBarTranslucent
            onRequestClose={close}
            presentationStyle={'overFullScreen'}
            statusBarTranslucent
            transparent
            visible
          >
            <SelectPaymentMethodScreen />
          </NativeModal>
        ))}
    </SelectPaymentMethodContext.Provider>
  )
}

function SelectPaymentMethodScreen() {
  const router = useRouter()
  const user = AuthContext.useSelector((state) => state.context.values?.user)
  const {
    type,
    close: closeScreen,
    isHidden,
    hide,
    offset,
    selectedBuyPayment,
    setSelectedBuyPayment,
    selectedBuyPaymentText,
    setSelectedBuyPaymentText,
    selectedSellPayment,
    setSelectedSellPayment,
    show
  } = useContext(SelectPaymentMethodContext)
  const { openModal, closeModal } = useModal()
  const { bottom } = useSafeAreaInsets()
  const { t } = useTranslation()
  const toast = useToast()
  const [tentativeSelectedBuyPayment, setTentativeSelectedBuyPayment] = useState<undefined | CurrentBuyPayment>(
    selectedBuyPayment
  )
  const [tentativeSelectedBuyPaymentText, setTentativeSelectedBuyPaymentText] = useState(selectedBuyPaymentText)
  const [tentativeSelectedSellPayment, setTentativeSelectedSellPayment] =
    useState<CurrentSellPayment>(selectedSellPayment)

  const { loading: rawDataLoading, data: rawData, refetch } = useQuery(GET_USER_PAYMENT_METHODS)

  const { data: userData, loading: userDataLoading } = useQuery(GET_USER, {
    variables: { id: user?.id }
  })

  const viewStyle = useAnimatedStyle(() => {
    return { transform: [{ translateX: offset.value }] }
  })

  function handleAddPaymentMethod(paymentMethodsCount: number) {
    if (!userData) {
      return
    }
    if (paymentMethodsCount >= 20) {
      toast.show({ variant: 'error', description: t('paymentMethod:maximumTwenty') })
      return
    }
    if (userData.user.withdrawalPasswordEnabled) {
      if (Platform.OS === 'web') {
        SheetManager.show('add-payment-method', {
          payload: {
            onClose: hide
          }
        })
        return
      }

      hide()
      setTimeout(() => {
        SheetManager.show('add-payment-method', {
          payload: {
            onCancel: () => setTimeout(show, 200)
          }
        })
      }, 0)
    } else {
      hide()
      openModal({
        children: (
          <SetTxPasswordModal
            onCancel={() => {
              closeModal()
              show()
            }}
            onSubmit={() => {
              closeModal()
              setTimeout(() => {
                router.push('/(main)/setting/tx-password')
              }, 200)
            }}
          />
        ),
        onClose: () => {
          closeModal()
          show()
        }
      })
    }
  }

  useEffect(() => {
    offset.value = withTiming(0, { duration: 300 })
  }, [])

  const onBuyChange = (id: string, kind: PaymentKind) => {
    const newSelectedPayment: CurrentBuyPayment = { id, kind }

    setTentativeSelectedBuyPayment(newSelectedPayment)

    // HACK TODO: Revisit this.
    let selectedPaymentInfo = ''

    const kindName = PAYMENT_METHODS[kind]
    // console.log({ rawData })
    const info = groupPaymentMethods(rawData)[kindName].find((val: Record<string, any>) => val.id === id)

    if (info) {
      const name = kind === PaymentKind.bank ? 'bankName' : 'name'
      selectedPaymentInfo = `${t(`payment:${PaymentKind[kind]}`)}(${info.info[kindName][name]})`
    }

    setTentativeSelectedBuyPaymentText(selectedPaymentInfo)
  }

  const isBuySelected = (id: string) => {
    return !!tentativeSelectedBuyPayment && tentativeSelectedBuyPayment.id === id
  }

  const onSellChange = (id: string, kind: PaymentKind) => {
    let newSelectedPayment: CurrentSellPayment
    if (tentativeSelectedSellPayment[kind] && tentativeSelectedSellPayment[kind]?.id === id) {
      newSelectedPayment = {
        ...tentativeSelectedSellPayment,
        [kind]: undefined
      }
    } else {
      newSelectedPayment = {
        ...tentativeSelectedSellPayment,
        [kind]: {
          id,
          kind
        }
      }
    }
    setTentativeSelectedSellPayment(newSelectedPayment)
  }

  const isSellSelected = (id: string) => {
    return (
      !!tentativeSelectedSellPayment &&
      Object.values(tentativeSelectedSellPayment).some((payment) => payment?.id === id)
    )
  }

  const confirm = () => {
    closeScreen()
    if (type === 'buy') {
      setSelectedBuyPayment(tentativeSelectedBuyPayment)
      setSelectedBuyPaymentText(tentativeSelectedBuyPaymentText)
    } else {
      setSelectedSellPayment(tentativeSelectedSellPayment)
    }
  }

  if (rawDataLoading || userDataLoading) {
    return (
      <Animated.View style={[styles.overlay, isHidden && styles.hidden, viewStyle]}>
        <NavigationBar handleAction={closeScreen} hasBottomBorder={true}>
          {t(type === 'buy' ? 'c2c:selectPaymentMethod' : 'c2c:selectReceivingMethod')}
        </NavigationBar>
        <View className={'flex-1 items-center justify-center bg-background-secondary'}>
          <View className={'animate-spin'}>
            <Spinner className={'size-6 text-primary'} />
          </View>
        </View>
      </Animated.View>
    )
  }

  if (!rawData || !userData) {
    return (
      <Animated.View style={[styles.overlay, isHidden && styles.hidden, viewStyle]}>
        <NavigationBar handleAction={closeScreen} hasBottomBorder={true}>
          {t(type === 'buy' ? 'c2c:selectPaymentMethod' : 'c2c:selectReceivingMethod')}
        </NavigationBar>
        <View className={'flex-1 items-center justify-center bg-background-secondary'}>
          <View className={'items-center gap-4'}>
            <ServerError className={'text-text-quaternary'} />
            <Text className={'text-sm text-text-secondary'}>{t('c2c:serverError')}</Text>
            <Button size={'md'} intent={'primary'} variant={'solid'} onPress={() => refetch()}>
              {t('paymentMethod:refresh')}
            </Button>
          </View>
        </View>
      </Animated.View>
    )
  }

  const tentativeSelectedSellPaymentCount = Object.values(tentativeSelectedSellPayment).filter(
    (payment) => payment !== undefined
  ).length
  const tentativeSelectedSellPaymentText = t('paymentMethod:selectedReceivingMethodCount', {
    count: tentativeSelectedSellPaymentCount
  })

  const filteredData =
    type === 'buy' ? filterPaymentMethods(rawData, userData.user.firstname, userData.user.lastname) : rawData

  const data = groupPaymentMethods(filteredData)
  const paymentMethodsCount = Object.values(rawData.getUserPaymentMethods).length

  return (
    <Animated.View style={[styles.overlay, isHidden && styles.hidden, viewStyle]}>
      <NavigationBar
        handleAction={closeScreen}
        hasBottomBorder={type === 'sell'}
        rightElement={
          <Pressable
            className={'items-center justify-center p-2'}
            onPress={() => handleAddPaymentMethod(paymentMethodsCount)}
          >
            <Text className={'text-base font-medium text-text-tertiary'}>{t('paymentMethod:add')}</Text>
          </Pressable>
        }
      >
        {t(type === 'buy' ? 'c2c:selectPaymentMethod' : 'c2c:selectReceivingMethod')}
      </NavigationBar>
      {type === 'buy' && (
        <View className={'bg-background-primary px-4 pb-4'}>
          <Banner mood={'info'}>
            {t('paymentMethod:matchingNameHint')}
          </Banner>
        </View>
      )}
      {filteredData.getUserPaymentMethods.length === 0 ? (
        <>
          <View className={'flex-1 items-center justify-center gap-4 bg-background-secondary'}>
            <Wallet className={'size-20 text-text-quaternary'} />
            <Text className={'text-sm text-text-secondary'}>{t('paymentMethod:noPaymentMethods')}</Text>
            <Button size={'md'} onPress={() => handleAddPaymentMethod(paymentMethodsCount)}>
              {t('paymentMethod:addPaymentMethod')}
            </Button>
          </View>
        </>
      ) : (
        <View className={'flex-1'}>
          <ScrollView className={'bg-background-secondary'}>
            {PAYMENT_METHODS.map((paymentMethod) => {
              if (!data[paymentMethod] || data[paymentMethod].length === 0) {
                return null
              }

              return (
                <View key={paymentMethod}>
                  <SectionHeader>{t(`payment:${paymentMethod}`)}</SectionHeader>
                  {data[paymentMethod].map((method: PaymentMethod, index: number) => {
                    const info = method.info[paymentMethod]

                    return (
                      <PaymentListItem
                        hasBorderTop={index === 0}
                        isBorderBottomInset={index !== data[paymentMethod].length - 1}
                        label={method.kind === PaymentKind.bank ? (info.bankName ?? '') : info.name}
                        value={info.account}
                        key={index}
                        onPress={
                          type === 'buy'
                            ? () => onBuyChange(method.id, method.kind)
                            : () => onSellChange(method.id, method.kind)
                        }
                        isChecked={type === 'buy' ? isBuySelected(method.id) : isSellSelected(method.id)}
                        type={type}
                      />
                    )
                  })}
                </View>
              )
            })}
          </ScrollView>
        </View>
      )}
      <View
        className={'w-full gap-6 border-t border-border-10 bg-background-primary p-4'}
        style={{ paddingBottom: bottom + 16, height: 120 + bottom }}
      >
        <View className={'flex-row justify-between'}>
          <Text className={'text-base text-text-secondary'}>{t('paymentMethod:selected')}</Text>
          <Text className={'text-base text-text-primary'}>
            {type === 'buy' ? tentativeSelectedBuyPaymentText : tentativeSelectedSellPaymentText}
          </Text>
        </View>
        <Button
          size={'md'}
          onPress={confirm}
          disabled={type === 'buy' ? !tentativeSelectedBuyPayment : !tentativeSelectedSellPaymentCount}
        >
          {t('paymentMethod:confirm')}
        </Button>
      </View>
    </Animated.View>
  )
}

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 10
  },
  hidden: {
    zIndex: -10
  }
})

function filterPaymentMethods(data: Record<string, any>, firstName: string | null, lastName: string | null) {
  if (firstName === null) {
    firstName = ''
  }
  if (lastName === null) {
    lastName = ''
  }
  const possibleNames = [
    `${firstName.toLowerCase()}${lastName.toLowerCase()}`,
    `${lastName.toLowerCase()}${firstName.toLowerCase()}`
  ]
  return {
    getUserPaymentMethods: data.getUserPaymentMethods.filter((method: Record<string, any>) => {
      const key = PaymentKind[method.kind]
      const paymentMethodName = method.info[key].name.toLowerCase().replaceAll(' ', '')
      return possibleNames.includes(paymentMethodName)
    })
  }
}

interface PaymentListItemProps extends PressableProps {
  hasBorderTop: boolean
  isBorderBottomInset: boolean
  isChecked?: boolean
  label: string
  value: string
  type: 'buy' | 'sell'
}

function PaymentListItem({
  hasBorderTop,
  isBorderBottomInset,
  isChecked = false,
  label,
  value,
  type,
  ...props
}: PaymentListItemProps) {
  return (
    <Pressable {...props}>
      {({ pressed }) => (
        <View
          className={cn(
            'h-12 border-border-10 bg-background-primary',
            pressed && 'bg-background-secondary',
            hasBorderTop && 'border-t',
            !isBorderBottomInset && 'border-b'
          )}
        >
          <View
            className={cn(
              'ml-4 h-full flex-row items-center justify-between pr-4',
              isBorderBottomInset && 'border-b border-border-10'
            )}
          >
            <View className={'flex-row gap-3'}>
              {type === 'buy' && (isChecked ? <Check className={'text-primary'} /> : <View className={'size-6'} />)}
              {type === 'sell' &&
                (isChecked ? (
                  <View className={'m-[-2px]'}>
                    <CheckCircle className={'size-7 text-primary'} />
                  </View>
                ) : (
                  <View className={'size-6 rounded-xl border border-text-tertiary'} />
                ))}
              <Text className={'text-base text-text-primary'}>{label}</Text>
            </View>
            <Text className={'text-base text-text-primary'}>{`*${value.slice(-4)}`}</Text>
          </View>
        </View>
      )}
    </Pressable>
  )
}

type SelectPaymentMethodTriggerProps = {
  type: 'buy' | 'sell'
  value: string
  onPress?: () => void
}

export function SelectPaymentMethodTrigger({ type, value, onPress }: SelectPaymentMethodTriggerProps) {
  const { t } = useTranslation()
  const { open, setType } = useContext(SelectPaymentMethodContext)
  const shownValue = value || t(type === 'buy' ? 'c2c:selectPaymentMethod' : 'c2c:selectReceivingMethod')

  function handlePress() {
    Keyboard.dismiss()
    open()
    setType(type)
  }
  return (
    <View className={'gap-1'}>
      <Text className={'text-sm font-medium text-text-secondary'}>
        {t(type === 'buy' ? 'orders:paymentMethod' : 'orders:paymentMethods')}
      </Text>
      <Pressable onPress={onPress || handlePress}>
        {({ pressed }) => (
          <View
            className={cn(
              'h-11 flex-row items-center rounded-lg bg-fill-subtle p-3',
              pressed && 'bg-fill-subtle-active'
            )}
          >
            <Text className={cn('mr-auto text-sm text-text-primary', value === '' && 'text-text-quaternary')}>
              {shownValue}
            </Text>
            <ChevronRight className={'text-text-quaternary'} />
          </View>
        )}
      </Pressable>
      <View className={'h-4'} />
    </View>
  )
}
